import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { createPopper, type Instance, type Placement } from '@popperjs/core'
import classNames from 'classnames'
import { v4 as uuid } from 'uuid'

import styles from './RhapHoverTooltip.module.scss'

// Default z-index set to be above Agent Chat panel (z-index: 1400)
const DEFAULT_TOOLTIP_Z_INDEX = 1500

// Popper offset: [x-axis offset, y-axis offset] in pixels
// 8px vertical offset provides spacing between trigger and tooltip
const TOOLTIP_OFFSET: [number, number] = [0, 8]

export interface RhapHoverTooltipProps {
  content?: string | React.ReactNode
  enabled?: boolean
  noWrap?: boolean
  placement?: Placement
  trigger: (props: {
    'aria-describedby': string
    onBlur: () => void
    onFocus: () => void
    onMouseEnter: () => void
    onMouseLeave: () => void
    ref: React.Ref<HTMLElement>
  }) => React.ReactElement
  variant?: 'default' | 'dark'
  zIndex?: number
}

/**
 * A hover tooltip that matches KibTooltip styling, but triggers on hover instead of click.
 */
const RhapHoverTooltip: React.FC<RhapHoverTooltipProps> = ({
  trigger,
  content,
  enabled = true,
  noWrap = false,
  placement = 'top',
  variant = 'default',
  zIndex = DEFAULT_TOOLTIP_Z_INDEX,
}) => {
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null)
  const [tooltipElement, setTooltipElement] = useState<HTMLDivElement | null>(
    null,
  )
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null)
  const [isHovered, setIsHovered] = useState(false)
  const popperInstanceRef = useRef<Instance | null>(null)
  const tooltipIdRef = useRef(`rhap-tooltip-${uuid()}`)

  useEffect(() => {
    if (!triggerElement || !tooltipElement) {
      return undefined
    }

    popperInstanceRef.current = createPopper(triggerElement, tooltipElement, {
      placement,
      modifiers: [
        { name: 'offset', options: { offset: TOOLTIP_OFFSET } },
        ...(arrowElement
          ? [{ name: 'arrow', options: { element: arrowElement } }]
          : []),
      ],
    })

    return () => {
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy()
        popperInstanceRef.current = null
      }
    }
  }, [triggerElement, tooltipElement, arrowElement, placement])

  const handleShow = () => setIsHovered(true)
  const handleHide = () => setIsHovered(false)

  if (!enabled || !content) {
    return trigger({
      ref: () => {},
      onFocus: () => {},
      onBlur: () => {},
      onMouseEnter: () => {},
      onMouseLeave: () => {},
      'aria-describedby': tooltipIdRef.current,
    })
  }

  return (
    <>
      {trigger({
        ref: setTriggerElement,
        onFocus: handleShow,
        onBlur: handleHide,
        onMouseEnter: handleShow,
        onMouseLeave: handleHide,
        'aria-describedby': tooltipIdRef.current,
      })}
      {isHovered &&
        createPortal(
          <div
            className={classNames(styles.tooltip, {
              [styles.tooltipDark]: variant === 'dark',
            })}
            id={tooltipIdRef.current}
            ref={setTooltipElement}
            role="tooltip"
            style={{
              zIndex,
              ...(noWrap && { whiteSpace: 'nowrap', maxWidth: 'none' }),
            }}
            onMouseEnter={handleShow}
            onMouseLeave={handleHide}
          >
            {content}
            {variant !== 'dark' && (
              <div className={styles.arrow} ref={setArrowElement} />
            )}
          </div>,
          document.body,
        )}
    </>
  )
}

export default RhapHoverTooltip
